import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '../../application/ports/api';
import { useDocumentTitle } from '../app/useDocumentTitle';
import { useAuth } from '../app/useAuth';
import { FullscreenSpinner } from '../components/FullscreenSpinner';
import { errorMessage, useT } from '../i18n/i18n';
import { AuthLayout } from './auth/AuthLayout';
import styles from './auth/AuthLayout.module.css';

// Links are single-use: remember the redemption so a re-run effect does not spend it twice.
const redeemed = new Map<string, Promise<string>>();

/** Landing page of the emailed link: redeems the token and continues to `next`. */
export function VerifyLinkPage() {
  const t = useT();
  useDocumentTitle(t('nav.login'));
  const { verifyLink } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!token) {
      setError(t('errors.link_invalid'));
      return;
    }
    let promise = redeemed.get(token);
    if (!promise) {
      promise = verifyLink(token);
      redeemed.set(token, promise);
    }
    let active = true;
    promise.then(
      (next) => active && navigate(next, { replace: true }),
      (err: unknown) =>
        active &&
        setError(
          err instanceof ApiError && err.code === 'link_invalid'
            ? t('errors.link_invalid')
            : errorMessage(t, err),
        ),
    );
    return () => {
      active = false;
    };
  }, [token, verifyLink, navigate, t]);

  if (!error) return <FullscreenSpinner />;
  return (
    <AuthLayout title={t('auth.linkInvalidTitle')}>
      <div className={styles.formError} role="alert">
        {error}
      </div>
      <p className={styles.switch}>
        <Link to="/login">{t('auth.requestNewLink')}</Link>
      </p>
    </AuthLayout>
  );
}
